import { FC, useCallback, useMemo, useState, useEffect } from 'react'
import { useFormContext } from 'react-hook-form'
import { DataGrid, GridColDef } from '@microservices/wiskey-react-table-component'
import { Box, Button, IconButton } from '@mui/material'
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material'
import { v4 as uuidv4 } from 'uuid'
import { MobileSearchAssistant, SearchFilter } from '@mobileTypes'
import { useFetchAllObjectQuery, useFetchObjectByCodeQuery } from '@redux/api'
import { AutocompleteOption } from '@types'

import { AddSimpleFilterDialog, SearchFilterForm } from './AddSimpleFilterDialog'

const SimpleFiltersConfigTab: FC = () => {
  const { watch, setValue } = useFormContext<MobileSearchAssistant>()
  const filters = watch('filters') || []
  const objectCode = watch('objectCode')


  const [dialogState, setDialogState] = useState<{
    open: boolean
    isEdit: boolean
    initialData?: Partial<SearchFilterForm>
  }>({
    open: false,
    isEdit: false,
  })

  const { data: objectsResponse } = useFetchAllObjectQuery()
  const objectOptions: AutocompleteOption[] = useMemo(
    () =>
      objectsResponse?.map(obj => ({
        id: obj.code,
        label: `${obj.title} (${obj.code})`,
      })) || [],
    [objectsResponse]
  )

  const handleAdd = useCallback(() => {
    setDialogState({
      open: true,
      isEdit: false,
    })
  }, [])

  const [editingFilterIndex, setEditingFilterIndex] = useState<number | null>(null)
  const [editingObjectCode, setEditingObjectCode] = useState<string | null>(null)

  const { data: editingObjectData } = useFetchObjectByCodeQuery(editingObjectCode || '', {
    skip: !editingObjectCode,
  })

  const handleEdit = useCallback(
    (index: number) => {
      const filter = filters[index]
      if (filter) {
        const savedObjectCode = filter.config?.objectCode
        const targetObjectCode = savedObjectCode || objectCode

        const objectOption = objectOptions.find(opt => opt.id === targetObjectCode)

        const finalObjectOption = objectOption || (objectOptions.length > 0 ? objectOptions[0] : null)
        const finalObjectCode = finalObjectOption?.id as string || targetObjectCode

        setEditingFilterIndex(index)
        setEditingObjectCode(finalObjectCode || null)

        const formData: Partial<SearchFilterForm> = {
          id: filter.id,
          code: filter.id,
          title: filter.label,
          objectTitle: finalObjectOption?.label || '',
          object: finalObjectOption || null,
          isPinning: false,
          visibility: true,
          value: {
            name: filter.config?.valueNameString || null, // Восстанавливаем путь Value как строку
            fieldType: null,
            formatDate: null,
            objectValue: filter.config?.objectValueString || '', // Восстанавливаем путь ObjectValue как строку
            linkedObjectCode: '',
            isDefaultPlaceholder: true,
            placeholderValue: filter.config?.placeholder || filter.config?.defaultValue || '',
            placeholderEnd: '',
            placeholderStart: '',
            isPinning: false,
            dropDownListCode: null,
            dropDownWindowCode: null,
            dropDownListFilters: [],
            dropDownWindowFilters: [],
            asCheckbox: filter.config?.asCheckbox || false,
            asDuration: false,
            pathArray: filter.config?.pathArray || [], // Восстанавливаем путь для ObjectValue (массив)
            valuePathArray: filter.config?.valuePathArray || [], // Восстанавливаем путь для Value (массив)
            min: -24,
            max: 48,
            timeUnit: { id: 'HOUR', label: 'HOUR' },
          },
        }

        setDialogState({
          open: true,
          isEdit: true,
          initialData: formData,
        })
      }
    },
    [filters, objectOptions, objectCode]
  )

  useEffect(() => {
    if (
      editingFilterIndex !== null &&
      editingObjectData &&
      dialogState.isEdit &&
      dialogState.open &&
      filters[editingFilterIndex]
    ) {
      const filter = filters[editingFilterIndex]
      const fieldNameToFind = filter.config?.valueName || filter.fieldCode
      const field = editingObjectData.fields?.find(
        f => String(f.id) === String(fieldNameToFind) || f.name === fieldNameToFind
      )

      if (field && dialogState.initialData) {
        const savedValuePathArray = filter.config?.valuePathArray || []
        const savedObjectValuePathArray = filter.config?.pathArray || []

        const valuePathArrayToUse = savedValuePathArray.length > 0
          ? savedValuePathArray
          : dialogState.initialData.value?.valuePathArray || []

        const objectValuePathArrayToUse = savedObjectValuePathArray.length > 0
          ? savedObjectValuePathArray
          : dialogState.initialData.value?.pathArray || []

        const savedName = dialogState.initialData.value?.name || filter.config?.valueNameString || field.name

        const updatedInitialData: Partial<SearchFilterForm> = {
          ...dialogState.initialData,
          value: {
            ...dialogState.initialData.value,
            name: savedName,
            valuePathArray: valuePathArrayToUse,
            pathArray: objectValuePathArrayToUse,
            valueType: field.valueType,
          } as any,
        }

        setDialogState(prev => ({
          ...prev,
          initialData: updatedInitialData,
        }))
      }
    }
  }, [editingFilterIndex, editingObjectData, dialogState.isEdit, dialogState.open, filters])

  const handleDelete = useCallback(
    (index: number) => {
      const updatedFilters = filters.filter((_, idx) => idx !== index)
      const finalFilters = updatedFilters.map((f, idx) => ({ ...f, order: idx }))
      setValue('filters', finalFilters, { shouldDirty: true })
    },
    [filters, setValue]
  )

  const handleSave = useCallback(
    (formData: SearchFilterForm) => {
      const valueNameRaw = formData.value?.name
      const objectCodeToSave = (formData.object?.id as string) || objectCode

      let fieldName: string = ''
      let fieldId: string | number = ''

      const valuePathArray = formData.value?.valuePathArray || []
      const objectValuePathArray = formData.value?.pathArray || []

      const valuePath = valuePathArray
        .map((obj: any) => obj?.field)
        .filter(Boolean)
        .join('.')
      const objectValuePath = objectValuePathArray
        .map((obj: any) => obj?.field)
        .filter(Boolean)
        .join('.')

      if (valuePathArray.length > 0) {
        const lastPathItem = valuePathArray[valuePathArray.length - 1]
        if (lastPathItem?.field) {
          fieldName = lastPathItem.field
          fieldId = lastPathItem.model || lastPathItem.field || ''
        } else if (typeof valueNameRaw === 'string') {
          fieldName = valueNameRaw
          fieldId = valueNameRaw
        }
      } else if (typeof valueNameRaw === 'string') {
        fieldName = valueNameRaw
        fieldId = valueNameRaw
      } else if (valueNameRaw && typeof valueNameRaw === 'object' && 'label' in valueNameRaw) {
        fieldName = valueNameRaw.label as string
        fieldId = valueNameRaw.id || valueNameRaw.label || ''
      } else {
        return
      }

      if (!fieldName) {
        return
      }

      const currentFilters = watch('filters') || []

      const searchFilter: SearchFilter = {
        id: formData.id || formData.code || uuidv4(),
        order: dialogState.isEdit
          ? currentFilters.findIndex((f: SearchFilter) => f.id === formData.id) >= 0
            ? currentFilters.findIndex((f: SearchFilter) => f.id === formData.id)
            : currentFilters.length
          : currentFilters.length,
        type: 'SEARCHBAR',
        label: formData.title,
        fieldCode: fieldName,
        operator: '==',
        config: {
          placeholder: formData.value?.placeholderValue || '',
          defaultValue: formData.value?.placeholderValue || '',
          valueName: fieldName,
          valueId: fieldId,
          valueType: formData.value?.valueType,
          objectCode: objectCodeToSave,
          valuePathArray: valuePathArray,
          pathArray: objectValuePathArray,
          valueNameString: valuePath || (typeof valueNameRaw === 'string' ? valueNameRaw : undefined),
          objectValueString: objectValuePath,
          asCheckbox: formData.value?.asCheckbox || false,
        },
      }

      if (dialogState.isEdit && formData.id) {
        const index = currentFilters.findIndex((f: SearchFilter) => f.id === formData.id)
        if (index !== -1) {
          const updatedFilters = [...currentFilters]
          updatedFilters[index] = searchFilter
          const finalFilters = updatedFilters.map((f: SearchFilter, idx: number) => ({ ...f, order: idx }))
          setValue('filters', finalFilters, { shouldDirty: true })
        } else {
          const updatedFilters = [...currentFilters, searchFilter]
          const finalFilters = updatedFilters.map((f: SearchFilter, idx: number) => ({ ...f, order: idx }))
          setValue('filters', finalFilters, { shouldDirty: true })
        }
      } else {
        const updatedFilters = [...currentFilters, searchFilter]
        const finalFilters = updatedFilters.map((f: SearchFilter, idx: number) => ({ ...f, order: idx }))
        setValue('filters', finalFilters, { shouldDirty: true })
      }

      setDialogState({ open: false, isEdit: false })
    },
    [dialogState, setValue, objectCode, watch]
  )

  const handleClose = useCallback(() => {
    setDialogState({ open: false, isEdit: false })
  }, [])

  const existingCodes = useMemo(
    () => filters.map(f => f.id).filter(Boolean) as string[],
    [filters]
  )

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'label',
        headerName: 'Label',
        flex: 1,
        minWidth: 200,
      },
      {
        field: 'fieldCode',
        headerName: 'Field Code',
        flex: 1,
        minWidth: 200,
      },
      {
        field: 'actions',
        headerName: 'Actions',
        width: 120,
        sortable: false,
        renderCell: params => (
          <Box>
            <IconButton size='small' onClick={() => handleEdit(params.row.index)}>
              <EditIcon />
            </IconButton>
            <IconButton size='small' color='error' onClick={() => handleDelete(params.row.index)}>
              <DeleteIcon />
            </IconButton>
          </Box>
        ),
      },
    ],
    [handleEdit, handleDelete]
  )

  const rows = useMemo(
    () =>
      filters.map((filter, index) => ({
        ...filter,
        id: filter.id || `filter-${index}`,
        index,
      })),
    [filters]
  )

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, gap: 2 }}>
      <Box>
        <Button variant='contained' startIcon={<AddIcon />} onClick={handleAdd}>
          Add Filter
        </Button>
      </Box>

      <Box sx={{ height: 400, width: '100%' }}>
        <DataGrid rows={rows} columns={columns} hideFooter disableRowSelectionOnClick />
      </Box>

      <AddSimpleFilterDialog
        open={dialogState.open}
        isEdit={dialogState.isEdit}
        initialData={dialogState.initialData}
        objectOptions={objectOptions}
        onClose={() => {
          setEditingFilterIndex(null)
          setEditingObjectCode(null)
          handleClose()
        }}
        onSave={handleSave}
      />
    </Box>
  )
}

export default SimpleFiltersConfigTab
